/**
 * Campaign Budget Optimizer Service
 * Calculates optimal daily budget based on campaign type and goals
 */

// Constants
const COST_PER_HIRE = 50;
const ATTRITION_RATE = 0.05; // 5% monthly attrition
const RECOVERY_MULTIPLIER = 2.5; // Target 2.5x attrition for recovery

/**
 * Calculate days between two dates
 */
function daysBetween(startDate: string | Date, endDate: string | Date): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  const diffMs = end.getTime() - start.getTime();
  return Math.max(1, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

/**
 * Optimize daily budget for Goal-based campaigns
 * 
 * @param goalType - Type of goal: 'date', 'hires', or 'budget'
 * @param goalValue - The target value (days for date, number for hires, amount for budget)
 * @param startDate - Campaign start date
 * @returns Optimized daily budget (rounded to nearest dollar)
 */
export function optimizeGoal(
  goalType: 'date' | 'hires' | 'budget',
  goalValue: number | string,
  startDate: string | Date
): number {
  if (goalType === 'date') {
    // Calculate days remaining between today and end date
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysRemaining = daysBetween(today, goalValue as string);
    
    // Assume TargetHires = 10 for prototype
    const targetHires = 10;
    const dailyBudget = (targetHires / daysRemaining) * COST_PER_HIRE;
    
    return Math.round(dailyBudget);
  }
  
  if (goalType === 'hires') {
    // Assume 30-day target timeline
    const targetDays = 30;
    const hiresGoal = Number(goalValue);
    const dailyBudget = (hiresGoal / targetDays) * COST_PER_HIRE;
    
    return Math.round(dailyBudget);
  }
  
  if (goalType === 'budget') {
    // Optimize for speed: spend budget in 2 weeks (14 days)
    const budgetGoal = Number(goalValue);
    const dailyBudget = budgetGoal / 14;
    
    return Math.round(dailyBudget);
  }
  
  return 0;
}

/**
 * Optimize daily budget for Perpetual campaigns
 * Calculates based on maintaining headcount within bounds with attrition consideration
 * 
 * @param lowerBound - Minimum coverage percentage
 * @param upperBound - Maximum coverage percentage
 * @param currentHeadcount - Current staff count (optional, defaults to 100)
 * @returns Optimized daily budget (rounded to nearest dollar)
 */
export function optimizePerpetual(
  lowerBound: number,
  upperBound: number,
  currentHeadcount: number = 100
): number {
  // Use upper bound as target to maintain buffer
  const targetHeadcount = upperBound;
  
  // Calculate monthly attrition
  const monthlyLoss = targetHeadcount * ATTRITION_RATE;
  
  // Calculate daily attrition
  const dailyLoss = monthlyLoss / 30;
  
  // Target recovery rate (hire 2.5x to stay ahead)
  const dailyHireTarget = dailyLoss * RECOVERY_MULTIPLIER;
  
  // Calculate daily budget
  const dailyBudget = dailyHireTarget * COST_PER_HIRE;
  
  return Math.round(dailyBudget);
}

/**
 * Get a human-readable explanation of the optimization
 */
export function getOptimizationExplanation(
  campaignType: 'goal' | 'perpetual',
  goalType?: 'date' | 'hires' | 'budget',
  result?: number
): string {
  if (campaignType === 'perpetual') {
    return `Optimized for ${RECOVERY_MULTIPLIER}x attrition recovery rate (${(ATTRITION_RATE * 100).toFixed(0)}% monthly attrition).`;
  }
  
  if (goalType === 'date') {
    return `Optimized to reach hiring goal by target date (${COST_PER_HIRE}$/hire).`;
  }
  
  if (goalType === 'hires') {
    return `Optimized for 30-day target timeline (${COST_PER_HIRE}$/hire).`;
  }
  
  if (goalType === 'budget') {
    return `Optimized to spend budget efficiently over 2 weeks.`;
  }
  
  return 'Budget optimized for efficiency.';
}
